import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  Fragment,
} from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  Card,
  Form,
  FloatingLabel,
  Button,
  Spinner,
  Row,
  Col,
  Modal,
  InputGroup,
} from "react-bootstrap";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { ArrowLeft, Trash2 } from "react-feather";
import Select from "react-select"; // For multi-select dropdowns

import apiService from "../../services/apiService";
import { useNotification } from "../../components/notification";
import { formatTime, generateCode } from "../../utils";

// Define validation schema using yup
const schema = yup.object({
  name: yup.string().required("Name is required"),
  description: yup.string().notRequired(),
  applyType: yup.string().required("Apply Type is required"),
  type: yup.string().required("Type is required"),
  discountScope: yup.string().required("Discount Scope is required"),
  //roles: yup.array().min(1, "At least one role is required"),
  scope: yup.string().required("Scope is required"),
  categoryId: yup.string().when("scope", {
    is: (val) => val === "category" || val === "product",
    then: (schema) => schema.required("Category is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  productIds: yup.array().when("scope", {
    is: "product",
    then: (schema) => schema.min(1, "At least one product is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  code: yup.string().required("Code is required"),
  value: yup
    .number()
    .typeError("Value must be a number")
    .required("Value is required")
    .positive("Value must be positive"),
  startDate: yup.string().required("Start Date is required"),
  endDate: yup.string().required("End Date is required"),
  //usageLimit: yup.number().required("Usage Limit is required"),
  isActive: yup.boolean().required("Status is required"),
  applyDefaultDiscount: yup
    .boolean()
    .required("ApplyDefaultDiscount is required"),
  volumeBasedDiscount: yup.boolean(),
  // volumeScope removed from schema
  volumeDiscountRules: yup.object().when("volumeBasedDiscount", {
    is: true,
    then: () =>
      yup.object({
        rules: yup
          .array()
          .of(
            yup.object({
              minVolume: yup
                .number()
                .typeError("Minimum volume must be a number")
                .required("Minimum volume is required")
                .positive("Must be positive"),
              maxVolume: yup
                .number()
                .typeError("Maximum volume must be a number")
                .required("Maximum volume is required")
                .positive("Must be positive")
                .moreThan(
                  yup.ref("minVolume"),
                  "Max volume must be greater than min volume"
                ),
              pricePerLiter: yup
                .number()
                .typeError("Price per liter must be a number")
                .required("Price per liter is required")
                .positive("Price per liter must be positive"),
            })
          )
          .min(1, "At least one rule is required"),
      }),
    otherwise: () => yup.object().notRequired(),
  }),
});

const AddDiscount = () => {
  const { discountId } = useParams(); // Get discount ID from URL for editing
  const isEditing = !!discountId; // Check if editing
  const didFetch = useRef(false);
  const initialLoadRef = useRef(true);
  // Removed unused roles state
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [categories, setCategories] = useState([]); // List of categories
  const [products, setProducts] = useState([]); // List of products
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);

  const [roles, setRoles] = useState([]); // List of roles

  const [volumeBasedDiscount, setVolumeBasedDiscount] = useState(false);
  const [volumeRules, setVolumeRules] = useState([]);
  // const [discountScope, setDiscountScope] = useState(""); // Removed unused state variable, discountScope is handled by react-hook-form

  // React Hook Form setup
  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: "",
      description: "",
      applyType: "automatic",
      type: "percentage",
      //roles: [],
      scope: "all",
      categoryId: "",
      productIds: [],
      code: "",
      value: 0,
      startDate: "",
      endDate: "",
      //usageLimit: 100,
      isActive: true,
      applyDefaultDiscount: true,
    },
  });
  const today = new Date().toISOString().split("T")[0];
  // Watch scope to conditionally render fields
  const scope = watch("scope");
  const categoryId = watch("categoryId");
  const discountType = watch("type");
  const watchedDiscountScope = watch("discountScope"); // Watch the global discountScope

  useEffect(() => {
    if (discountType === "percentage") {
      setVolumeBasedDiscount(false);
      setValue("volumeBasedDiscount", false, { shouldValidate: true });
      setVolumeRules([]);
      // Clear volumeDiscountRules in the form state (it's an object in the schema)
      setValue("volumeDiscountRules", {}, { shouldValidate: true });
    }
  }, [discountType, setValue, setVolumeBasedDiscount, setVolumeRules]);

  const addVolumeRule = () => {
    const newRule = {
      id: Date.now(),
      minVolume: "",
      maxVolume: "",
      pricePerLiter: "", // Changed from incrementPerLiter, removed discountPercentage/Amount
    };
    setVolumeRules([...volumeRules, newRule]);
  };

  const removeVolumeRule = (id) => {
    setVolumeRules(volumeRules.filter((rule) => rule.id !== id));
  };

  const updateVolumeRule = (id, field, value) => {
    setVolumeRules(
      volumeRules.map((rule) =>
        rule.id === id ? { ...rule, [field]: value } : rule
      )
    );
  };

  // Fetch roles
  const fetchRoles = useCallback(async () => {
    try {
      const response = await apiService.get("/role?isDropdown=true");
      setRoles(response);
    } catch (error) {
      showNotification("danger", "Failed to fetch roles.");
    }
  }, [showNotification]);

  // Fetch roles
  const fetchCategories = useCallback(async () => {
    try {
      const response = await apiService.get("/category/all/names");
      setCategories(response);
    } catch (error) {
      showNotification("danger", "Failed to fetch categories.");
    }
  }, [showNotification]);

  // Fetch products
  const fetchProducts = useCallback(
    async (search) => {
      try {
        if (!search || !categoryId) return;
        setIsLoadingProducts(true); // Set loading state
        const response = await apiService.get(
          `/products/search-by-name?search=${search}&categoryId=${categoryId}`
        );
        // Transform fetched products into the correct format
        const formattedProducts = response.map((product) => ({
          value: product.id, // Use `id` as `value`
          label: product.name, // Use `name` as `label`
        }));

        // Always include the "All" option in the products list
        setProducts([{ value: "all", label: "All" }, ...formattedProducts]);
      } catch (error) {
        showNotification("danger", "Failed to fetch products.");
      } finally {
        setIsLoadingProducts(false); // Reset loading state
      }
    },
    [categoryId, showNotification]
  );

  const mapDiscountResponseToFormData = (response) => {
    console.log("Response", response);
    return {
      name: response.name,
      description: response.description,
      applyType: response.applyType,
      type: response.type,
      roles: response.selectedRoles,
      scope: response.scope,
      categoryId: response.categoryId,
      productIds: response.selectedProducts,
      code: response.code,
      value: parseFloat(response.value),
      startDate: formatTime(response.startDate, "ISO"),
      endDate: formatTime(response.endDate, "ISO"),
      usageLimit: response.usageLimit,
      isActive: response.isActive,
      applyDefaultDiscount: response.applyDefaultDiscount,

      volumeBasedDiscount: response.volumeDiscountRules !== null,
      discountScope: response.discountScope || "",
      volumeRules:
        response.volumeDiscountRules?.rules.map((rule, index) => ({
          id: `${Date.now()}-${index}`,
          minVolume: rule.minVolume,
          maxVolume: rule.maxVolume,
          pricePerLiter: rule.pricePerLiter,
        })) || [],
    };
  };

  // Fetch discount details for editing
  const fetchDiscount = useCallback(async () => {
    try {
      setIsPageLoading(true);
      const response = await apiService.get(`/discounts/${discountId}`);
      const formattedData = mapDiscountResponseToFormData(response);
      reset(formattedData); // Pre-populate form with discount data

      setVolumeBasedDiscount(formattedData.volumeBasedDiscount);
      setVolumeRules(formattedData.volumeRules);
      setValue("discountScope", formattedData.discountScope); // Set discountScope using react-hook-form
    } catch (error) {
      showNotification("danger", "Failed to fetch discount details.");
    } finally {
      setIsPageLoading(false);
    }
  }, [discountId, reset, showNotification]);

  // Fetch data on component mount
  useEffect(() => {
    if (didFetch.current) return; // Prevent second call
    didFetch.current = true;

    const fetchData = async () => {
      setIsPageLoading(true);
      await fetchCategories();
      await fetchRoles();
      setIsPageLoading(false);
    };

    fetchData();
    if (isEditing) {
      fetchDiscount();
    }
  }, [fetchCategories, fetchRoles, fetchDiscount, isEditing]);

  // To handle category changes for product scope
  useEffect(() => {
    if (scope === "product" && categoryId) {
      // When category changes in product scope, reset product selection
      // Don't reset on the initial load
      if (!initialLoadRef.current) {
        // When category changes in product scope, reset product selection
        setValue("productIds", []);
      } else {
        // Mark that initial load is complete
        initialLoadRef.current = false;
      }
    }
  }, [categoryId, scope, setValue]);

  // Effect to automatically set 'Eligible Products' scope and category
  // based on the global 'Discount Scope'
  useEffect(() => {
    if (watchedDiscountScope === "PER_PRODUCT") {
      setValue("scope", "product"); // Set 'Eligible Products' scope to 'product'

      // Set category to the second item if available, otherwise the first
      if (categories && categories.length > 0) {
        const categoryToSelect =
          categories.length > 1 ? categories[1].id : categories[0].id;
        setValue("categoryId", categoryToSelect);
      } else {
        // Optionally, clear categoryId if no categories are available or handle as needed
        // setValue("categoryId", "");
      }
    } else if (watchedDiscountScope === "CART_TOTAL") {
      setValue("scope", "all"); // Set 'Eligible Products' scope to 'all'
      // Optionally, you might want to clear categoryId and productIds if switching to 'CART_TOTAL'
      // setValue("categoryId", "");
      // setValue("productIds", []);
    }
    // If discountScope changes to something else (e.g., "ALL_PRODUCTS"), you might want to add logic here
    // to reset scope, categoryId, and productIds, or leave them as they were manually set.
  }, [watchedDiscountScope, setValue, categories]);

  // Handle form submission
  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      const formattedData = {
        ...data, // data from react-hook-form already contains discountScope
        volumeBasedDiscount,
        volumeDiscountRules: volumeBasedDiscount
          ? {
              rules: volumeRules.map((rule) => ({
                minVolume: parseFloat(rule.minVolume),
                maxVolume: parseFloat(rule.maxVolume),
                pricePerLiter: parseFloat(rule.pricePerLiter),
              })),
            }
          : null,
      };

      // Rules based on scope
      if (formattedData.scope === "all") {
        formattedData.categoryId = "";
        formattedData.productIds = ["all"];
      } else if (formattedData.scope === "category") {
        // Keep the selected categoryId
        formattedData.productIds = ["all"]; // Products under this category
      } else if (formattedData.scope === "product") {
        // Keep the selected categoryId (it's used to fetch products)
        // Convert product selection from react-select format to array of strings
        formattedData.productIds = Array.isArray(formattedData.productIds)
          ? formattedData.productIds.map((item) => item.value)
          : [];
      }

      // Ensure roles is an array of strings, defaulting to ["all"] if not provided or empty
      formattedData.roles =
        Array.isArray(data.roles) && data.roles.length > 0
          ? data.roles.map((role) =>
              typeof role === "object" ? role.value : role
            )
          : ["all"];

      formattedData.usageLimit = parseInt(data.usageLimit, 10) || 10000;

      if (isEditing) {
        // Update existing discount
        await apiService.put(`/discounts/${discountId}`, formattedData);
        showNotification("success", "Discount updated successfully!");
      } else {
        // Create new discount
        await apiService.post("/discounts", formattedData);
        showNotification("success", "Discount created successfully!");
      }
      navigate("/discounts"); // Redirect to discounts list
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to save discount.";
      showNotification("danger", errorMessage);
      console.error("Failed to save discount:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Fragment>
      <Card className="p-4 rounded-4">
        <Card.Body>
          <div className="d-flex align-items-center justify-content-between mb-2">
            <div>
              <h1 className="mb-2 fs-5">
                {isEditing ? "Edit Discount" : "Add Discount"}
              </h1>
              <p className="mb-0 text-light-emphasis fw-light">
                {isEditing
                  ? "Update the discount details below."
                  : "Please enter the discount details below."}
              </p>
            </div>
            <Link variant="light" to="/discounts" className="btn btn-light">
              <ArrowLeft size={18} /> Back
            </Link>
          </div>

          <hr className="mt-3 mb-4" />
          <Form onSubmit={handleSubmit(onSubmit)}>
            <h6 className="mb-3">General Information</h6>
            <Row>
              <Col sm={6} md={6} lg={4} className="mb-3">
                <FloatingLabel label="Name">
                  <Form.Control
                    type="text"
                    placeholder="Name"
                    {...register("name")}
                    isInvalid={!!errors.name}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.name?.message}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Col>

              <Col sm={6} className="mb-3">
                <FloatingLabel label="Description">
                  <Form.Control
                    as="textarea"
                    placeholder="Description"
                    {...register("description")}
                    isInvalid={!!errors.description}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.description?.message}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Col>
            </Row>
            <h6 className="my-4">Validity</h6>
            <Row>
              <Col sm={12} md={6} lg={4} className="mb-3">
                <FloatingLabel label="Start Date">
                  <Form.Control
                    type="date"
                    placeholder="Start Date"
                    {...register("startDate")}
                    min={today}
                    isInvalid={!!errors.startDate}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.startDate?.message}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Col>

              <Col sm={12} md={6} lg={4} className="mb-3">
                <FloatingLabel label="End Date">
                  <Form.Control
                    type="date"
                    placeholder="End Date"
                    {...register("endDate")}
                    min={today}
                    isInvalid={!!errors.endDate}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.endDate?.message}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Col>
            </Row>

            <h6 className="my-4">Discount Details</h6>
            <Row>
              <Col sm={12} md={6} lg={4} className="mb-3">
                <FloatingLabel label="Discount Scope">
                  <Form.Select
                    {...register("discountScope")}
                    isInvalid={!!errors.discountScope}
                  >
                    <option value="CART_TOTAL">Cart Total</option>
                    <option value="PER_PRODUCT">Per Product</option>
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.discountScope?.message}
                  </Form.Control.Feedback>
                  <Form.Text className="text-muted">
                    'Cart' applies to total volume in cart. 'Product' applies to
                    individual product quantities.
                  </Form.Text>
                </FloatingLabel>
              </Col>
            </Row>
            <Row className="align-items-center">
              <Col sm={12} md={6} lg={4} className="mb-3">
                <FloatingLabel label="Apply Type">
                  <Form.Select
                    {...register("applyType")}
                    isInvalid={!!errors.applyType}
                  >
                    <option value="automatic">Automatic</option>
                    <option value="coupon">Coupon</option>
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.applyType?.message}
                  </Form.Control.Feedback>
                  <Form.Text className="text-muted">
                    How the Discount is Applied
                  </Form.Text>
                </FloatingLabel>
              </Col>

              <Col sm={12} md={6} lg={4} className="mb-3">
                <FloatingLabel label="Discount Type">
                  <Form.Select {...register("type")} isInvalid={!!errors.type}>
                    <option value="percentage">Percentage</option>
                    <option value="fixed">Fixed Amount</option>
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.type?.message}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Col>
              <Col sm={12} md={6} lg={4} className="mb-3">
                <FloatingLabel label="Discount Value">
                  <Form.Control
                    type="number"
                    placeholder="Value"
                    {...register("value")}
                    isInvalid={!!errors.value}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.value?.message}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Col>
              {/* <Col sm={12} md={6} lg={4} className="mb-3">
                <FloatingLabel label="Usage Limit">
                  <Form.Control
                    type="number"
                    placeholder="Usage Limit"
                    {...register("usageLimit")}
                    isInvalid={!!errors.usageLimit}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.usageLimit?.message}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Col> */}
              <Col sm={12} md={6} lg={4} className="mb-3">
                <Form.Group controlId="code">
                  <InputGroup>
                    <Form.Control
                      type="text"
                      placeholder="Enter or generate code"
                      {...register("code")}
                      isInvalid={!!errors.code}
                    />
                    <Button
                      variant="outline-secondary"
                      onClick={() => {
                        setValue("code", generateCode());
                      }}
                    >
                      Generate Automatically
                    </Button>
                    {errors.code && (
                      <Form.Control.Feedback type="invalid">
                        {errors.code.message}
                      </Form.Control.Feedback>
                    )}
                  </InputGroup>
                </Form.Group>
              </Col>
              <Col
                sm={12}
                md={6}
                lg={4}
                className="mb-3 d-flex flex-row align-items-center justify-content-between"
                // justify-content-between: Pushes first item to start, last item to end
              >
                {/* Left Switch */}
                <Form.Check
                  type="switch"
                  id="isActive"
                  label="Activate Discount"
                  name="isActive"
                  {...register("isActive")}
                />

                {/* Right Switch */}
                <div title="Enable this option to allow other default discounts to apply alongside this discount for the cart or product.">
                  <Form.Check
                    type="switch"
                    id="applyDefaultDiscount"
                    label="Allow Default Discount"
                    name="applyDefaultDiscount"
                    {...register("applyDefaultDiscount")}
                    // title="Enable this option to allow other default discounts to apply alongside this discount for the cart or product."
                  />
                </div>
              </Col>
            </Row>
            <h6 className="my-4">Volume-Based Discount</h6>
            {discountType === "percentage" && (
              <Form.Text className="text-muted d-block mb-2">
                Note: Volume-based discounts are only available for 'Fixed Amount' discount types. Please adjust the 'Discount Type' above to enable this option.
              </Form.Text>
            )}
            <Row className="mb-3">
              <Col>
                <Form.Check
                  type="switch"
                  id="volumeBasedDiscount"
                  label="Activate Volume-Based Discount"
                  checked={volumeBasedDiscount}
                  disabled={discountType === "percentage"}
                  onChange={(e) => {
                    const newCheckedState = e.target.checked;
                    setVolumeBasedDiscount(newCheckedState);
                    setValue("volumeBasedDiscount", newCheckedState, { shouldValidate: true });
                    if (newCheckedState) {
                      // If activating and no rules exist, add an initial rule
                      if (volumeRules.length === 0) {
                        addVolumeRule();
                      }
                    } else {
                      // If deactivating, clear existing rules
                      setVolumeRules([]);
                      setValue("volumeDiscountRules", {}, { shouldValidate: true });
                    }
                  }}
                />
              </Col>
            </Row>
            {volumeBasedDiscount && (
              <div className="p-3 border rounded">
                <h6 className="mb-3">Volume-Based Discount Rules</h6>

                {/* Volume Rules */}
                {volumeRules.map((rule) => (
                  <Row key={rule.id} className="mb-3 p-3 border rounded mx-0">
                    <Col sm={12} md={6} lg={3} className="my-1">
                      <Form.Group>
                        <Form.Label>Minimum Volume (liters)</Form.Label>
                        <Form.Control
                          type="number"
                          value={rule.minVolume}
                          onChange={(e) =>
                            updateVolumeRule(
                              rule.id,
                              "minVolume",
                              e.target.value
                            )
                          }
                          isInvalid={!rule.minVolume && volumeRules.length > 0}
                        />
                        <Form.Control.Feedback type="invalid">
                          Required
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col sm={12} md={6} lg={3} className="my-1">
                      <Form.Group>
                        <Form.Label>Maximum Volume (liters)</Form.Label>
                        <Form.Control
                          type="number"
                          value={rule.maxVolume}
                          onChange={(e) =>
                            updateVolumeRule(
                              rule.id,
                              "maxVolume",
                              e.target.value
                            )
                          }
                          isInvalid={!!errors.volumeDiscountRules?.rules?.[volumeRules.indexOf(rule)]?.maxVolume}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.volumeDiscountRules?.rules?.[volumeRules.indexOf(rule)]?.maxVolume?.message || "Required"}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    {/* Discount Percentage and Discount Amount columns removed */}

                    <Col sm={12} md={6} lg={3} className="my-1"> 
                      <Form.Group>
                        <Form.Label>$ Per Liter</Form.Label>
                        <Form.Control
                          type="number"
                          value={rule.pricePerLiter} 
                          onChange={(e) =>
                            updateVolumeRule(
                              rule.id,
                              "pricePerLiter", 
                              e.target.value
                            )
                          }
                          isInvalid={!!errors.volumeDiscountRules?.rules?.[volumeRules.indexOf(rule)]?.pricePerLiter}
                        />
                        <Form.Control.Feedback type="invalid">
                           {errors.volumeDiscountRules?.rules?.[volumeRules.indexOf(rule)]?.pricePerLiter?.message || "Required"}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    <Col
                      sm={12}
                      md={6}
                      lg={1}
                      className="d-flex align-items-center"
                    >
                      <Button
                        variant="danger"
                        size="sm"
                        title="Remove Rule"
                        onClick={() => removeVolumeRule(rule.id)}
                        className="my-1"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </Col>
                  </Row>
                ))}

                <Button
                  variant="primary"
                  onClick={addVolumeRule}
                  className="me-2"
                >
                  Add Rule
                </Button>

                {volumeBasedDiscount && volumeRules.length === 0 && (
                  <div className="text-danger mt-2">
                    At least one rule is required
                  </div>
                )}
              </div>
            )}
            <h6 className="my-4">Eligible Products</h6>
            <Row className="align-items-center">
              <Col sm={12} md={6} lg={4} className="mb-3">
                <FloatingLabel label="Scope">
                  <Form.Select
                    {...register("scope")}
                    isInvalid={!!errors.scope}
                    disabled={
                      watchedDiscountScope === "CART_TOTAL" ||
                      watchedDiscountScope === "PER_PRODUCT"
                    }
                  >
                    <option value="all">All</option>
                    <option value="category">Category</option>
                    <option value="product">Product</option>
                  </Form.Select>
                  {watchedDiscountScope === "CART_TOTAL" && (
                    <Form.Text className="text-muted">
                      This is set to 'All' as the discount applies to the 'Cart
                      Total'. To target specific products or categories, please
                      adjust the 'Discount Scope' field above.
                    </Form.Text>
                  )}
                  {watchedDiscountScope === "PER_PRODUCT" && (
                    <Form.Text className="text-muted">
                      This is set to 'Product' as the discount applies 'Per
                      Product'. To change this, please adjust the 'Discount
                      Scope' field above.
                    </Form.Text>
                  )}
                  <Form.Control.Feedback type="invalid">
                    {errors.scope?.message}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Col>

              {(scope === "category" || scope === "product") && (
                <Col sm={12} md={6} lg={4} className="mb-3">
                  <FloatingLabel label="Category">
                    <Form.Select
                      {...register("categoryId")}
                      isInvalid={!!errors.categoryId}
                      disabled={watchedDiscountScope === "PER_PRODUCT"}
                    >
                      <option value="">Select Category</option>
                      {categories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </Form.Select>
                    {watchedDiscountScope === "PER_PRODUCT" && (
                      <Form.Text className="text-muted">
                        A category is auto-selected as this discount applies
                        'Per Product'. To change this, please adjust the
                        'Discount Scope' field above.
                      </Form.Text>
                    )}
                    <Form.Control.Feedback type="invalid">
                      {errors.categoryId?.message}
                    </Form.Control.Feedback>
                  </FloatingLabel>
                </Col>
              )}

              {scope === "product" && categoryId && (
                <Col sm={12} md={6} lg={4} className="mb-3">
                  <Controller
                    name="productIds"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={products}
                        value={field.value}
                        isMulti
                        placeholder="Search Products"
                        onInputChange={(inputValue) => {
                          fetchProducts(inputValue); // Fetch products based on user input
                        }}
                        onChange={(selectedOptions) => {
                          // Handle "All" selection logic
                          if (
                            selectedOptions.some(
                              (option) => option.value === "all"
                            )
                          ) {
                            // If "All" is selected, clear other selections
                            field.onChange([{ value: "all", label: "All" }]);
                          } else {
                            // If specific products are selected, ensure "All" is not selected
                            field.onChange(
                              selectedOptions.filter(
                                (option) => option.value !== "all"
                              )
                            );
                          }
                        }}
                        isLoading={isLoadingProducts} // Show loading indicator
                      />
                    )}
                  />
                  {errors.productIds && (
                    <div className="text-danger">
                      {errors.productIds.message}
                    </div>
                  )}
                </Col>
              )}
              {/* <Col sm={12} md={6} lg={4} className="mb-3">
                <Controller
                  name="roles"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      options={roles}
                      isMulti
                      placeholder="Select Roles"
                      value={field.value}
                      onChange={(selectedOptions) => {
                        if (
                          selectedOptions.some(
                            (option) => option.value === "all"
                          )
                        ) {
                          field.onChange([{ value: "all", label: "All" }]);
                        } else {
                          field.onChange(
                            selectedOptions.filter(
                              (option) => option.value !== "all"
                            )
                          );
                        }
                      }}
                    />
                  )}
                />
                {errors.roles && (
                  <div className="text-danger">{errors.roles.message}</div>
                )}
              </Col> */}
            </Row>

            <Button
              type="submit"
              disabled={isLoading}
              className="btn-main mt-4"
            >
              {isLoading ? (
                <Spinner animation="border" size="sm" />
              ) : isEditing ? (
                "Update Discount"
              ) : (
                "Add Discount"
              )}
            </Button>
          </Form>
        </Card.Body>
      </Card>

      <Modal
        show={isPageLoading}
        aria-labelledby="Loading"
        className="modal-loading"
        contentClassName="bg-transparent border-0"
        centered
      >
        <Modal.Body className="text-center">
          <Spinner animation="border" role="status" variant="primary">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </Modal.Body>
      </Modal>
    </Fragment>
  );
};

export default AddDiscount;
